import { VariableStateId } from '@/typeAlias'
import { ParameterizationType, Variable } from '@/types/variable'

export const ParameterizationWithIcons = {
  general: { icon: 'G', name: 'General' },
  noisyAnd: { icon: 'A', name: 'Noisy AND' },
  noisyOr: { icon: 'O', name: 'Noisy OR' },
  unknown: { icon: '?', name: 'Unknown' },
  irrelevant: { icon: '', name: 'Irrelevant (no parent)' },
} as const

const getParameterization = (
  parameterization: ParameterizationType,
  stateIds: Array<VariableStateId>,
  stateNames: Array<string>,
  optimalStateId: VariableStateId | null,
  probabilities: Array<number>,
) => {
  if (stateNames.some((name) => !name)) {
    return ParameterizationWithIcons.unknown
  }
  switch (parameterization) {
    case ParameterizationType.General:
      return ParameterizationWithIcons.general
    case ParameterizationType.NoisyMax:
      if (!hasParent(probabilities, stateIds))
        return ParameterizationWithIcons.irrelevant
      return getNoisyMaxSubtype(stateIds, stateNames, optimalStateId)
  }
}

export const getDetailedParameterizationType = (
  variable: Variable,
  stateNames: Array<string>,
) =>
  getParameterization(
    variable.parameterizationType,
    variable.stateIds,
    stateNames,
    variable.optimalStateId,
    variable.probabilities,
  )

export default getParameterization

export function getNoisyMaxSubtype(
  stateIds: Array<VariableStateId>,
  stateNames: Array<string>,
  optimalStateId: VariableStateId | null,
): (typeof ParameterizationWithIcons)[keyof typeof ParameterizationWithIcons] {
  if (stateNames[0].toLowerCase() === 'present')
    return ParameterizationWithIcons.noisyOr
  if (stateNames[stateNames.length - 1].toLowerCase() === 'present')
    return ParameterizationWithIcons.noisyAnd
  if (optimalStateId) {
    if (isFirstState(stateIds, optimalStateId))
      return ParameterizationWithIcons.noisyAnd
    if (isLastState(stateIds, optimalStateId))
      return ParameterizationWithIcons.noisyOr
  }
  return ParameterizationWithIcons.unknown
}

function isFirstState(
  stateIds: Array<VariableStateId>,
  optimalStateId: VariableStateId,
) {
  return stateIds[0] === optimalStateId
}

function isLastState(
  stateIds: Array<VariableStateId>,
  optimalStateId: VariableStateId,
) {
  return stateIds[stateIds.length - 1] === optimalStateId
}

function hasParent(
  probabilities: Array<number>,
  stateIds: Array<VariableStateId>,
) {
  return probabilities.length > stateIds.length
}

export const isNoisySwitchEnabled = (parameterizationSubtypeName: string) =>
  parameterizationSubtypeName === ParameterizationWithIcons.noisyAnd.name ||
  parameterizationSubtypeName === ParameterizationWithIcons.noisyOr.name ||
  parameterizationSubtypeName === ParameterizationWithIcons.irrelevant.name ||
  parameterizationSubtypeName === ParameterizationWithIcons.unknown.name
